import { logger } from '@/lib/logger';
import { useToastStore } from '@src/stores/toastStore';
import { useAuthStore } from '@stores/authStore';
import { useUserSettingsStore } from '@stores/userSettingsStore';
import { webpushMutations } from '@therame/api-client';
import { Role } from '@therame/shared';
import { defineStore } from 'pinia';
import { ref } from 'vue';
export const usePushNotificationStore = defineStore('pushNotification', () => {
  const authStore = useAuthStore();
  const toastStore = useToastStore();
  const userSettingsStore = useUserSettingsStore();
  const pushNotificationActivated = ref<boolean>(false);
  const isSupported = ref<boolean>(false);
  const pushNotificationChecked = ref<boolean>(false);

  const requestPermissionPushNotification = async () => {
    if (!('serviceWorker' in navigator)) {
      logger.warn('No Support for service worker!');
      return;
    }
    if (!('Notification' in window)) {
      logger.warn('Push-Benachrichtigungen werden von deinem Browser nicht unterstützt.');
      return;
    }

    /**
     * shows a prompt to the user to allow or deny push notifications
     */
    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      logger.info('Push-Benachrichtigungen aktiviert');
      pushNotificationActivated.value = true;

      toastStore.addToast({
        text: 'Push-Benachrichtigungen aktiviert',
        type: 'success',
      });

      await subscribePush();
    } else if (permission === 'denied') {
      logger.info('Du hast Push-Benachrichtigungen abgelehnt.');
      pushNotificationActivated.value = false;
      await declinePush();
    } else {
      logger.info('Push-Benachrichtigungen wurden nicht aktiviert.');
      pushNotificationActivated.value = false;
      await declinePush();
    }
  };

  const getActiveServiceWorkerRegistration =
    async (): Promise<ServiceWorkerRegistration | null> => {
      if (!('serviceWorker' in navigator)) {
        return null;
      }

      try {
        // `navigator.serviceWorker.ready` NEVER resolves when no service worker
        // ever activates (e.g. `pnpm dev`, where the PWA SW is not served). Race
        // it against a short timeout so callers on a blocking path — notably
        // logout, which now awaits unsubscribePush() — can't hang forever. When a
        // SW is active (production) `.ready` resolves in a few ms, so this adds no
        // delay there; the timeout only bites when there is genuinely no SW.
        const registration = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 1500)),
        ]);

        if (!registration || !registration.active) {
          return null;
        }

        return registration;
      } catch (error) {
        logger.error({ msg: 'Failed to get service worker registration', error });
        return null;
      }
    };

  const checkDeviceHasSubscription = async (): Promise<boolean> => {
    if (!('PushManager' in window)) {
      return false;
    }

    if (!authStore.authContext?.user?.id) {
      return false;
    }

    try {
      const registration = await getActiveServiceWorkerRegistration();
      if (!registration) {
        return false;
      }

      const currentSubscription = await registration.pushManager.getSubscription();
      if (!currentSubscription) {
        return false;
      }

      const subscriptionData = currentSubscription.toJSON();
      const result = await webpushMutations.hasSpecificSubscription(
        subscriptionData as Record<string, unknown>,
      );
      return result === true;
    } catch (error) {
      logger.error({ msg: 'Failed to check device subscription', error });
      return false;
    }
  };

  // Backend heal gate, consulted only when this device has NO local subscription
  // (an iOS rotation can silently wipe it). True when the backend confirms the
  // CURRENT user still has a stored subscription AND still wants push — so we can
  // only ever restore the current user's own push, never inherit another user's.
  // The backend AND-check is what stops us re-enabling push after an iOS rotation
  // followed by an in-app opt-out (which leaves a stale stored subscription but
  // flips the push settings off). Backend errors (e.g. feature flag off) are
  // treated as "no", so we never heal on an unverifiable signal.
  const shouldRestoreSubscription = async (): Promise<boolean> => {
    try {
      return await webpushMutations.shouldRestoreSubscription();
    } catch (error) {
      logger.error({ msg: 'Failed to check whether to restore subscription', error });
      return false;
    }
  };

  // Called when the browser permission is 'granted'. Decides whether we may
  // (re)attach push to the CURRENT user on this device.
  const healOrPromptSwitch = async () => {
    const registration = await getActiveServiceWorkerRegistration();
    const deviceSubscription = registration
      ? await registration.pushManager.getSubscription()
      : null;

    if (deviceSubscription) {
      // This device already holds a subscription belonging to whichever user last
      // opted in on this browser. If that's the current user (e.g. the session
      // merely expired and they logged back in as themselves), heal silently. If
      // it belongs to someone else — a different account logged in without a clean
      // logout — we must NOT silently hand their push to the new user. Ask first.
      const ownedByCurrentUser = await checkDeviceHasSubscription();
      if (ownedByCurrentUser) {
        await subscribePush();
      } else if (authStore.authContext?.user?.id) {
        pushNotificationActivated.value = false;
        promptSubscriptionSwitch();
      } else {
        pushNotificationActivated.value = false;
      }
      return;
    }

    // No local subscription. The backend heal (keyed on the current user) may
    // still restore one after an iOS rotation — safe, because it can only ever
    // touch the current user's own stored subscription.
    if (await shouldRestoreSubscription()) {
      await subscribePush();
    } else {
      pushNotificationActivated.value = false;
    }
  };

  // The device already carries push for a DIFFERENT account (a previous session
  // that never logged out cleanly). We never move push silently — ask the user.
  // Accepting drops the old local subscription and creates a fresh one for the
  // current user; the old endpoint stays in the previous user's Redis record but
  // is now dead and gets pruned there on their next send.
  const promptSubscriptionSwitch = () => {
    toastStore.addToast({
      text: 'Push-Benachrichtigungen sind auf diesem Gerät noch für einen anderen Account aktiv. Möchtest du sie für diesen Account übernehmen?',
      type: 'info',
      buttonText: 'Wechseln',
      persist: true,
      buttonAction: () => {
        switchSubscriptionToCurrentUser().catch((error) =>
          logger.error({ msg: 'Failed to switch push subscription to current user', error }),
        );
      },
    });
  };

  const switchSubscriptionToCurrentUser = async () => {
    const registration = await getActiveServiceWorkerRegistration();
    if (!registration) {
      return;
    }

    // Drop the previous user's local subscription first. subscribePush() then
    // finds no local subscription and creates a fresh one saved under the current
    // user, opting them into their push settings.
    //
    // If unsubscribe() fails the old subscription is still live and still recorded
    // for the previous user — continuing would make subscribePush() reuse it and
    // save the very same endpoint under the current user, so both accounts could
    // push to this device. Abort instead.
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      const successful = await existing.unsubscribe();

      if (!successful) {
        logger.error({ msg: 'Failed to unsubscribe previous user subscription locally' });
        pushNotificationActivated.value = false;
        toastStore.addToast({
          text: 'Push-Benachrichtigungen konnten nicht übernommen werden. Bitte versuche es später erneut.',
          type: 'error',
        });
        return;
      }
    }

    await subscribePush();
  };

  const checkPermission = async () => {
    try {
      const pushSupported =
        'Notification' in window && 'PushManager' in window && 'serviceWorker' in navigator;

      if (!pushSupported) {
        isSupported.value = false;
        pushNotificationActivated.value = false;
        return;
      }

      isSupported.value = true;

      const permission = Notification.permission;

      if (permission === 'granted') {
        await healOrPromptSwitch();
      } else if (permission === 'denied') {
        // Denied means push is NOT active — the settings push column must stay
        // hidden. (This must be false, not true: a `granted`-that-became-`denied`
        // user has no working push.) The dashboard "activate push" CTA keys off
        // the same flag, so a denied user is offered it again — clicking simply
        // re-denies. That is the honest state; we do not fake activation to
        // suppress the prompt.
        pushNotificationActivated.value = false;
        await declinePush();
      } else {
        // 'default' — never subscribe automatically; creating a subscription needs
        // a user gesture, so we wait for an explicit opt-in.
        pushNotificationActivated.value = false;
      }
    } finally {
      pushNotificationChecked.value = true;
    }
  };

  const subscribePush = async () => {
    if (!('PushManager' in window)) {
      return;
    }

    const registration = await getActiveServiceWorkerRegistration();
    if (!registration) {
      return;
    }

    let subscription = await registration.pushManager.getSubscription();

    // If we don't have a subscription or it's expired, create a new one
    if (
      !subscription ||
      (subscription.expirationTime && subscription.expirationTime < Date.now())
    ) {
      updateUserSettings(true);

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(import.meta.env.VITE_VAPID_PUBLIC_KEY),
      });
    }

    // Always save the subscription to the backend (handles sync issues)
    try {
      await webpushMutations.saveSubscription({ subscription: subscription.toJSON() });
      pushNotificationActivated.value = true;
    } catch (error) {
      logger.error({ msg: 'Failed to send subscription', error });
    }
  };

  const updateUserSettings = async (newValue: boolean) => {
    if (!userSettingsStore.settings || !authStore.authContext?.user?.role) {
      return;
    }

    if ([Role.ADMIN, Role.MANAGEMENT].includes(authStore.authContext.user.role)) {
      userSettingsStore.setSetting('pushNotificationRegistrationEnabled', newValue);
    }
    if ([Role.THERAPIST, Role.ADMIN, Role.MANAGEMENT].includes(authStore.authContext.user.role)) {
      userSettingsStore.setSetting('pushNotificationPatientHandOverEnabled', newValue);
    }
    if (authStore.authContext.user.role !== Role.SUPERADMIN) {
      userSettingsStore.setSetting('pushNotificationAppointmentEnabled', newValue);
    }
    if (authStore.authContext.user.role !== Role.SUPERADMIN) {
      userSettingsStore.setSetting('pushNotificationExerciseProgramEnabled', newValue);
    }
    if (authStore.authContext.user.role !== Role.SUPERADMIN) {
      userSettingsStore.setSetting('pushNotificationTrainingReminderEnabled', newValue);
    }
    if ([Role.THERAPIST, Role.ADMIN, Role.MANAGEMENT].includes(authStore.authContext.user.role)) {
      userSettingsStore.setSetting('pushNotificationProgramStatsEnabled', newValue);
    }
    if ([Role.PREVENTION, Role.PATIENT].includes(authStore.authContext.user.role)) {
      userSettingsStore.setSetting('pushNotificationInvoicesEnabled', newValue);
    }
    if ([Role.ADMIN, Role.MANAGEMENT].includes(authStore.authContext.user.role)) {
      userSettingsStore.setSetting('pushNotificationShopEnabled', newValue);
    }

    await userSettingsStore.updateSettings();
  };

  // Drops THIS device's push subscription (locally + in the backend record).
  // Deliberately does not touch the user's push settings: those are per-user and
  // shared by every device they use, so a device-scoped cleanup — notably logout —
  // must never disable push on their other subscribed devices.
  const unsubscribePush = async () => {
    if (!('PushManager' in window)) {
      return;
    }

    const registration = await getActiveServiceWorkerRegistration();
    if (!registration) {
      return;
    }

    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      return;
    }

    const successful = await subscription.unsubscribe();

    if (!successful) {
      logger.error({ msg: 'Failed to unsubscribe locally' });
      return;
    }

    try {
      await webpushMutations.deleteSubscription(subscription.toJSON());
    } catch (error) {
      logger.error({ msg: 'Failed to remove subscription on server', error });
    }
  };

  // The browser refused push here (permission denied, or the prompt dismissed).
  // On top of dropping any local subscription this records the opt-out in the
  // user's push settings: unlike logout, a refusal is an explicit "I don't want
  // push" from the user, so the stored preference should follow it.
  const declinePush = async () => {
    await unsubscribePush();
    await updateUserSettings(false);
  };

  const urlBase64ToUint8Array = (base64String: any) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  };

  const resetPushNotificationState = () => {
    pushNotificationActivated.value = false;
    pushNotificationChecked.value = false;
  };

  return {
    pushNotificationActivated,
    isSupported,
    pushNotificationChecked,
    requestPermissionPushNotification,
    checkPermission,
    checkDeviceHasSubscription,
    unsubscribePush,
    resetPushNotificationState,
  };
});
